import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Swords, Users, CalendarClock } from "lucide-react";
import { getToolStats, subscribeToolStats } from "@/lib/toolStats";

// Libellé de la métrique principale, propre à chaque outil.
const PRIMARY = {
  deuxVoies: { fr: "duels menés", en: "debates held", es: "duelos librados", nl: "debatten gevoerd" },
  doute: { fr: "doutes traversés", en: "doubts faced", es: "dudas afrontadas", nl: "twijfels doorstaan" },
  proces: { fr: "procès rendus", en: "verdicts reached", es: "veredictos", nl: "vonnissen" },
};
const EXPLORERS = { fr: "explorateurs", en: "explorers", es: "exploradores", nl: "ontdekkers" };
const MONTH = { fr: "ce mois-ci", en: "this month", es: "este mes", nl: "deze maand" };
const LOCALE = { fr: "fr-FR", en: "en-US", es: "es-ES", nl: "nl-NL" };

function Stat({ value, label, Icon, live, locale, testId }) {
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testId}>
      <span className="relative flex-shrink-0 w-7 h-7 rounded-lg bg-primary/12 border border-primary/25 flex items-center justify-center text-primary">
        <Icon size={14} strokeWidth={1.8} />
        {live && (
          <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
        )}
      </span>
      <div className="leading-tight min-w-0">
        <motion.div
          key={value}
          initial={{ opacity: 0.4, y: -3 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="font-display text-base sm:text-lg text-foreground tabular-nums"
          data-testid={`${testId}-value`}
        >
          {value.toLocaleString(locale)}
        </motion.div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-foreground/45 truncate">{label}</div>
      </div>
    </div>
  );
}

// Bandeau de compteurs fictifs "live" affiché sur les cartes des outils dialectiques.
export function ToolCounters({ ns, className = "" }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const locale = LOCALE[lang] || LOCALE.fr;
  const [stats, setStats] = useState(() => getToolStats(ns));

  useEffect(() => subscribeToolStats(ns, setStats), [ns]);

  if (!stats) return null;
  const primary = (PRIMARY[ns] || PRIMARY.deuxVoies)[lang] || PRIMARY[ns]?.fr || "";

  return (
    <div
      data-testid={`tool-counters-${ns}`}
      className={`rounded-2xl border border-primary/20 bg-white/[0.03] px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${className}`}
    >
      <Stat value={stats.debats} label={primary} Icon={Swords} live locale={locale} testId={`tool-counter-${ns}-debats`} />
      <span className="hidden sm:block w-px h-7 bg-primary/15" aria-hidden />
      <Stat value={stats.explorateurs} label={(EXPLORERS[lang] || EXPLORERS.fr)} Icon={Users} locale={locale} testId={`tool-counter-${ns}-explorateurs`} />
      <span className="hidden sm:block w-px h-7 bg-primary/15" aria-hidden />
      <Stat value={stats.mois} label={(MONTH[lang] || MONTH.fr)} Icon={CalendarClock} locale={locale} testId={`tool-counter-${ns}-mois`} />
    </div>
  );
}
